/**
 * Gestionnaire de mise à jour incrémentale de l'état du jeu
 * Évite les fetchModel() inutiles en mettant à jour directement l'état local
 */

import { GAME_ACTIONS } from './constants.js';

/**
 * Met à jour l'état du modèle avec les données reçues du WebSocket
 * @param {Object} currentModel - Modèle actuel
 * @param {Object} gameData - Données reçues du WebSocket
 * @returns {Object} Nouveau modèle mis à jour
 */
export function updateGameState(currentModel, gameData) {
  if (!currentModel || !gameData) {
    return currentModel;
  }

  // Faire une copie shallow du modèle pour préserver toutes les propriétés de premier niveau
  // Les objets imbriqués seront partagés mais c'est OK car on ne modifie que sequenceActivities
  const updatedModel = { ...currentModel };
  
  // Préserver les tableaux et objets imbriqués en créant de nouvelles références
  if (currentModel.sequenceActivities && Array.isArray(currentModel.sequenceActivities)) {
    updatedModel.sequenceActivities = [...currentModel.sequenceActivities];
  }
  if (currentModel.typeTirages && Array.isArray(currentModel.typeTirages)) {
    updatedModel.typeTirages = [...currentModel.typeTirages];
  }
  if (currentModel.tirage) {
    updatedModel.tirage = { ...currentModel.tirage };
  }

  switch (gameData.action) {
    case GAME_ACTIONS.BALL:
      // Mettre à jour les boules tirées
      if (gameData.allBalls) {
        const balls = gameData.allBalls.split(',').filter(Boolean);
        const ballIds = balls.map((b) => parseInt(b));
        
        // Créer un map des boules existantes pour préserver leurs propriétés
        const existingBallsMap = new Map();
        if (updatedModel.sequenceActivities && Array.isArray(updatedModel.sequenceActivities)) {
          updatedModel.sequenceActivities.forEach((boule) => {
            if (boule.ballId) {
              existingBallsMap.set(boule.ballId, boule);
            }
          });
        }
        
        // Créer la nouvelle liste en préservant les propriétés existantes
        updatedModel.sequenceActivities = ballIds.map((ballId) => {
          const existingBall = existingBallsMap.get(ballId);
          // Préserver toutes les propriétés de la boule existante, ou créer une nouvelle
          return existingBall || { ballId: ballId };
        });
      }
      break;

    case GAME_ACTIONS.WINNER:
    case GAME_ACTIONS.UPDATE_WINNER:
      // Mettre à jour les gagnants
      // S'assurer que typeTirages reste un tableau
      if (gameData.typeTirages) {
        if (Array.isArray(gameData.typeTirages)) {
          updatedModel.typeTirages = [...gameData.typeTirages];
        } else {
          // Si ce n'est pas un tableau, préserver l'ancien ou utiliser un tableau vide
          console.warn('typeTirages n\'est pas un tableau, préservation de la valeur existante');
          // Ne pas modifier typeTirages si la valeur n'est pas valide
        }
      }
      break;

    // Pour les autres actions, on garde le modèle tel quel
    // car elles nécessitent un fetchModel complet
    default:
      break;
  }

  return updatedModel;
}

/**
 * Vérifie si une mise à jour incrémentale est possible
 * @param {string} action - Type d'action reçue
 * @returns {boolean} true si mise à jour incrémentale possible
 */
export function canUpdateIncrementally(action) {
  return [
    GAME_ACTIONS.BALL,
    GAME_ACTIONS.WINNER,
    GAME_ACTIONS.UPDATE_WINNER,
    GAME_ACTIONS.MESSAGE_PERSONNALISE,
  ].includes(action);
}

